import {
    HelpCircle,
    BrainCircuit,
    MessageSquare,
    Database,
    Hash,
    BookOpen,
    Sliders,
    Heart,
    Thermometer,
    Play,
    FileText
} from 'lucide-react';
import { Combobox } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { SliderWithInput } from "@/components/ui/slider-with-input";
import { SwitchWithLabel } from "@/components/ui/switch-with-label";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { getAllKnowledgeBases } from "@/api/knowledge-base/knowledge-base.api";
import type { KnowledgeBase } from "@/api/knowledge-base/knowledge-base.types";
import { useState, useEffect } from 'react';
import { Assistant } from "@/api/assistants/assistants.types";

interface BehaviorTabProps {
    assistant: Omit<Assistant, 'name'>;
    onUpdate: (updates: Partial<Assistant>) => void;
}

export function BehaviorTab({ assistant, onUpdate }: BehaviorTabProps) {
    const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
    const [isLoadingKnowledgeBases, setIsLoadingKnowledgeBases] = useState(false);

    useEffect(() => {
        const fetchKnowledgeBases = async () => {
            setIsLoadingKnowledgeBases(true);
            try {
                const { data, error } = await getAllKnowledgeBases();
                if (error) {
                    throw new Error(error);
                }
                if (data) {
                    setKnowledgeBases(data);
                }
            } catch {
                // Error handling if needed
            } finally {
                setIsLoadingKnowledgeBases(false);
            }
        };

        fetchKnowledgeBases();
    }, []);

    const handleModelUpdate = (field: keyof Assistant['model'], value: Assistant['model'][keyof Assistant['model']]) => {
        onUpdate({
            model: {
                ...assistant.model,
                [field]: value
            }
        });
    };

    const handleKnowledgeBaseUpdate = (knowledgeBaseId: string) => {
        onUpdate({
            model: {
                ...assistant.model,
                knowledgeBaseId: knowledgeBaseId === 'none' ? undefined : knowledgeBaseId
            }
        });
    };

    return (
        <div className="grid grid-cols-12 gap-6">
            {/* Left Column - Main Configuration */}
            <div className="col-span-8 space-y-6">
                <Card className="shadow-sm">
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <BrainCircuit className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <CardTitle>Assistant Behavior</CardTitle>
                                <CardDescription>Configure how your assistant thinks and responds</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* First Message */}
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <MessageSquare className="h-4 w-4 text-primary" />
                                <Label>First Message</Label>
                                <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-sm">
                                            <p>The first message that the assistant will say. This can also be a URL to a containerized audio file (mp3, wav, etc.).</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                            <Input
                                value={assistant.firstMessage || ''}
                                onChange={(e) => onUpdate({ firstMessage: e.target.value })}
                                placeholder="Enter the first message or audio URL"
                                className="h-9"
                            />
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <Play className="h-4 w-4 text-primary" />
                                <Label>First Message Mode</Label>
                            </div>
                            <Combobox
                                options={[
                                    { value: 'assistant-speaks-first', label: 'Assistant Speaks First' },
                                    { value: 'assistant-speaks-first-with-model-generated-message', label: 'Assistant Speaks First with Model-Generated Message' },
                                    { value: 'assistant-waits-for-user', label: 'Assistant Waits for User' }
                                ]}
                                value={assistant.firstMessageMode || 'assistant-speaks-first'}
                                onValueChange={(value) => onUpdate({ firstMessageMode: value })}
                                placeholder="Select first message mode..."
                                searchPlaceholder="Search modes..."
                                emptyMessage="No mode found."
                            />
                        </div>

                        {/* System Messages */}
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-primary" />
                                <Label>System Messages</Label>
                                <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-sm">
                                            <p>Messages that define the assistant's behavior and context. These are sent to the model before the conversation starts.</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                            <div className="space-y-2">
                                {assistant.model?.messages?.map((msg, index) => (
                                    <Textarea
                                        key={index}
                                        value={msg.content}
                                        onChange={(e) => {
                                            const newMessages = [...assistant.model.messages];
                                            newMessages[index] = { ...msg, content: e.target.value };
                                            handleModelUpdate('messages', newMessages);
                                        }}
                                        placeholder="Enter system instructions..."
                                        className="min-h-[100px]"
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Knowledge Base */}
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <BookOpen className="h-4 w-4 text-primary" />
                                <Label>Knowledge Bases</Label>
                                <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-sm">
                                            <p>Knowledge bases that the assistant can reference during conversations.</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>

                            {isLoadingKnowledgeBases ? (
                                <Skeleton className="h-9 w-full" />
                            ) : (
                                <Combobox
                                    options={[
                                        { value: 'none', label: 'None' },
                                        ...knowledgeBases.map((kb) => ({
                                            value: kb.id,
                                            label: kb.name,
                                            description: kb.id
                                        }))
                                    ]}
                                    value={assistant.model.knowledgeBaseId || 'none'}
                                    onValueChange={handleKnowledgeBaseUpdate}
                                    placeholder="Select a knowledge base..."
                                    searchPlaceholder="Search knowledge bases..."
                                    emptyMessage="No knowledge base found."
                                />
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Right Column - Parameters */}
            <div className="col-span-4 space-y-6">
                <Card className="shadow-sm">
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <Sliders className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 space-y-1">
                                <CardTitle>Response Parameters</CardTitle>
                                <CardDescription>Fine-tune the assistant's responses</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Temperature */}
                        <SliderWithInput
                            label="Temperature"
                            icon={Thermometer}
                            tooltip="Controls randomness in the model's responses. Higher values make the output more random, while lower values make it more focused and deterministic."
                            value={typeof assistant.model?.temperature === 'number' ? assistant.model.temperature : 0.7}
                            onChange={(value) => handleModelUpdate('temperature', value)}
                            min={0}
                            max={1}
                            step={0.1}
                            defaultValue={0.7}
                            formatValue={(v) => v.toFixed(1)}
                            decimalScale={1}
                            inputWidth={80}
                        />

                        {/* Max Tokens */}
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <Hash className="h-4 w-4 text-primary" />
                                <Label>Max Tokens</Label>
                                <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-sm">
                                            <p>This is the max number of tokens that the assistant will be allowed to generate in each turn of the conversation.</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                            <NumberInput
                                value={assistant.model?.maxTokens || 2000}
                                onValueChange={(value) => handleModelUpdate('maxTokens', value || 2000)}
                                min={1}
                                max={4000}
                                stepper={100}
                                decimalScale={0}
                            />
                        </div>

                        {/* Semantic Caching */}
                        <SwitchWithLabel
                            label="Semantic Caching"
                            icon={Database}
                            tooltip="When enabled, the assistant will store and reuse responses for similar questions, improving response time"
                            checked={assistant.model?.semanticCachingEnabled || false}
                            onCheckedChange={(checked) => handleModelUpdate('semanticCachingEnabled', checked)}
                        />

                        {/* Emotion Recognition */}
                        <SwitchWithLabel
                            label="Detect Emotion"
                            icon={Heart}
                            tooltip="Enable this property to detect user's emotion such as anger, joy etc. and feed it as an additional context to the model"
                            checked={assistant.model?.emotionRecognitionEnabled || false}
                            onCheckedChange={(checked) => handleModelUpdate('emotionRecognitionEnabled', checked)}
                        />
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
